// Namen, Standardwerte und Prüfungen für Ziele, Sendefenster und Berichte (Runde 10, Agent 10A).
// Die Prüfung der Ziele spiegelt `api/crm_api/workflows/ziele.py` (`probleme`): was hier fehlt, lehnt der Server beim Aktivieren ab.
import type { Sendefenster, Ziel, ZielArt } from "./teil2Api";

export const WOCHENTAGE_KURZ = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
export const WOCHENTAGE_LANG = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];

export const FENSTER_STANDARD: Sendefenster = {
  aktiv: false,
  tage: [0, 1, 2, 3, 4],
  von: "09:00",
  bis: "16:00",
  zeitzone: "organisation",
  streuung_min: 0,
  auch_aufgaben: false,
};

export const ZIELE: { art: ZielArt; label: string; text: string }[] = [
  { art: "status", label: "Statuswechsel", text: "Der Lead wechselt nach dem Einschreiben in einen bestimmten Status" },
  { art: "mail_eingang", label: "E-Mail erhalten", text: "Der Lead schreibt eine E-Mail" },
  { art: "sms_eingang", label: "SMS erhalten", text: "Der Lead schreibt eine SMS" },
  { art: "anruf", label: "Anruf", text: "Ein Anruf mit dem Lead, z. B. angenommen und mindestens 30 Sekunden" },
  { art: "custom_activity", label: "Custom Activity", text: "Eine bestimmte Custom Activity wird gespeichert, z. B. ein vereinbarter Termin" },
  { art: "bedingung", label: "Lead erfüllt Bedingung", text: "Der Lead hat bestimmte Werte, z. B. ein Feld ist ausgefüllt" },
];

export const zielInfo = (art: ZielArt) => ZIELE.find((z) => z.art === art) ?? ZIELE[0];

/** Anruf-Ergebnisse wie im Anruf-Protokoll (Close-Werte der Disposition). */
export const ANRUF_ERGEBNISSE: { wert: string; label: string }[] = [
  { wert: "answered", label: "Angenommen" },
  { wert: "no-answer", label: "Nicht erreicht" },
  { wert: "vm-answer", label: "Mailbox (Ansage)" },
  { wert: "vm-left", label: "Mailbox besprochen" },
  { wert: "busy", label: "Besetzt" },
  { wert: "blocked", label: "Blockiert" },
  { wert: "error", label: "Fehler" },
  { wert: "abandoned", label: "Abgebrochen" },
];

export function neueZielId(ziele: Ziel[]): string {
  const belegt = new Set(ziele.map((z) => z.id));
  let n = ziele.length + 1;
  while (belegt.has(`z${n}`)) n += 1;
  return `z${n}`;
}

export function neuesZiel(art: ZielArt, ziele: Ziel[]): Ziel {
  const config: Record<string, unknown> = (() => {
    switch (art) {
      case "status":
        return { nach: [] };
      case "anruf":
        return { richtung: "beide", disposition: ["answered"], min_dauer_s: null };
      case "custom_activity":
        return { type_id: null, bedingungen: [] };
      case "bedingung":
        return { bedingungen: [] };
      default:
        return {};
    }
  })();
  return { id: neueZielId(ziele), art, name: null, config };
}

/** Warum das Ziel noch nichts bewirken kann (spiegelt `ziele.probleme`), sonst null. */
export function zielProblem(z: Ziel): string | null {
  const c = z.config as { nach?: string[]; type_id?: string | null; bedingungen?: unknown[] };
  if (z.art === "status" && !(c.nach?.length ?? 0)) return "Es ist kein Status gewählt.";
  if (z.art === "custom_activity" && !c.type_id) return "Es ist kein Typ gewählt.";
  if (z.art === "bedingung" && !(c.bedingungen?.length ?? 0)) return "Es ist keine Bedingung eingetragen.";
  return null;
}

export function fensterText(f: Sendefenster): string {
  if (!f.aktiv) return "Immer";
  const tage = [...f.tage].sort((a, b) => a - b);
  const zusammenhaengend = tage.length > 2 && tage.every((t, i) => i === 0 || t === tage[i - 1] + 1);
  const wann = zusammenhaengend ? `${WOCHENTAGE_KURZ[tage[0]]} bis ${WOCHENTAGE_KURZ[tage[tage.length - 1]]}` : tage.map((t) => WOCHENTAGE_KURZ[t]).join(", ");
  return `${wann}, ${f.von} bis ${f.bis} Uhr`;
}

/** Dauer in Sekunden als „2 Std. 15 Min.“ oder „3 Tage 4 Std.“ (für Berichte, ganze Zahlen). */
export function dauerLang(sek: number | null | undefined): string {
  if (sek === null || sek === undefined) return "–";
  if (sek < 60) return `${sek} Sek.`;
  const tage = Math.floor(sek / 86400);
  const std = Math.floor((sek % 86400) / 3600);
  const min = Math.floor((sek % 3600) / 60);
  if (tage) return `${tage} ${tage === 1 ? "Tag" : "Tage"}${std ? ` ${std} Std.` : ""}`;
  if (std) return `${std} Std.${min ? ` ${min} Min.` : ""}`;
  return `${min} Min.`;
}

export const prozent = (q: number | null | undefined): string => (q === null || q === undefined ? "–" : `${String(q).replace(".", ",")} %`);

/** Zeitraum-Voreinstellungen der Berichte: Anzahl Tage bis einschließlich heute. */
export const ZEITRAEUME: { tage: number; label: string }[] = [
  { tage: 7, label: "7 Tage" },
  { tage: 30, label: "30 Tage" },
  { tage: 90, label: "90 Tage" },
  { tage: 365, label: "1 Jahr" },
];
